import { PageMeta } from '~/components/PageMeta.js';
import { PRIVACY_CONFIG, SITE } from '~/content/site.js';

function Clause({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mt-10 border-t border-line pt-6">
      <h2 className="display-3">{heading}</h2>
      <div className="mt-3 flex flex-col gap-3 leading-relaxed text-muted">{children}</div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
      <PageMeta
        title="Applicant privacy notice"
        description={'How ' + SITE.legalName + ' handles the information you provide when you apply.'}
        path="/privacy"
      />

      <p className="section-label">Applicant privacy</p>
      <h1 className="mt-4 display-2">What we do with the information you send us.</h1>
      <p className="mt-5 leading-relaxed text-muted">
        This notice covers information you provide when you apply for a role at {SITE.legalName}. It does not
        cover how the main {SITE.companyName} website handles visitors.
      </p>
      <p className="mt-3 text-sm text-muted">Last updated: {PRIVACY_CONFIG.lastUpdated}</p>

      <Clause heading="What we collect">
        <p>
          Only what you enter on the application form: your name, email address, and — if you choose to
          provide them — your phone number, current location, LinkedIn URL, and portfolio or personal site. We
          also store the resume file you upload, your cover letter if you write one, and your answers to any
          questions attached to the role.
        </p>
        <p>
          We do not ask for your Social Security number, government ID, date of birth, health or disability
          information, salary history, or a photograph, and you should not include them.
        </p>
      </Clause>

      <Clause heading="Why we use it">
        <p>
          To evaluate your application, to contact you about it, and to keep a record of hiring decisions. We
          do not use it to build a marketing list, we do not sell it, and we do not use it to train models.
        </p>
        <p>
          Applications are read by people at {SITE.legalName}. We do not automatically reject or score
          applications.
        </p>
      </Clause>

      <Clause heading="Who else sees it">
        <p>
          Service providers we use to run this site — hosting, database, file storage, and email delivery —
          process this information on our behalf and only for that purpose. We do not share applications with
          other companies.
        </p>
      </Clause>

      <Clause heading="How it is protected">
        <p>
          Applications are stored in a private database. Resume files are held in private storage and are only
          reachable through an authenticated, short-lived link available to the people at the studio who
          review applications. Access requires a signed-in account; there is no public sign-up.
        </p>
      </Clause>

      <Clause heading="How long we keep it">
        <p>
          We keep applications for {PRIVACY_CONFIG.retentionPeriod}, after which they and the uploaded resume
          are deleted. You can ask us to delete yours sooner.
        </p>
      </Clause>

      <Clause heading="Your choices">
        <p>
          You can ask us for a copy of what we hold about you, ask us to correct it, or ask us to delete it.
          Write to{' '}
          <a
            href={'mailto:' + PRIVACY_CONFIG.contactEmail}
            className="text-primary underline underline-offset-4"
          >
            {PRIVACY_CONFIG.contactEmail}
          </a>{' '}
          and include your application reference if you have one.
        </p>
        <p>{PRIVACY_CONFIG.postalAddress}</p>
      </Clause>

      <Clause heading="Changes to this notice">
        <p>
          If we change how applications are handled, we will update this page and the date at the top of it.
          Material changes to how we use information already submitted will be communicated to the people
          affected.
        </p>
      </Clause>
    </div>
  );
}
